import React, { FunctionComponent, ReactNode, useContext } from 'react'
import { useLoaderData, useMatch } from '@tanstack/react-router'
import { Button, Card, Collapse, List, Skeleton } from 'antd'
import type { CollapseProps } from 'antd'
import { formatTimestamp } from '../../utils/time'
import { useQuery } from '@tanstack/react-query'
import { useFeathersClient } from '@strac/react-client-feathers'
import { convertDMS, getDirection } from '../../utils/coordinate'
import { MapSettingsContext } from '../../contexts/MapSettingsContext'
import MapSettingsContextInterface from '../../contexts/MapSettingsContext/MapSettingsContextInterface'
import { CloseOutlined } from '@ant-design/icons'
import { describe } from 'vitest'
import { useMap } from '@vis.gl/react-google-maps'
import { canSeeVehicleLocationHistory } from '../../utils/auth'

interface IProps {
  userId: number
  vehicleId?: number
}

const VehicleWindow: FunctionComponent<any> = (props: IProps) => {
  const { userId } = props

  const match = useMatch({ strict: false })
  const { vehicles, vehicleLocations, agencies, vehicleTypes, deviceTypes } = useLoaderData({
    from: match.routeId ?? '/'
  })

  const { selectedVehicleId, setSelectedVehicleHistory, setSelectedVehicleId } = useContext(
    MapSettingsContext
  ) as MapSettingsContextInterface

  const map = useMap()

  const vehicleId = selectedVehicleId ?? props.vehicleId

  const vehicle = vehicles.find((vehicle: any) => vehicle.vehicle_id === vehicleId)
  const vehicleLocation = vehicleLocations.find((location: any) => location.vehicle_id === vehicleId)
  const agency = agencies.find((agency: any) => agency.id === vehicle?.agency_id)
  const vehicleType = vehicleTypes.find((vehicleType: any) => vehicleType.id === vehicle?.vehicle_type_id)
  const deviceType = deviceTypes.find((deviceType: any) => deviceType.id === vehicle?.device_type_id)

  const client = useFeathersClient()

  const { data: assettrakAccess } = useQuery({
    queryKey: ['assettrak-access', 'get', userId],
    queryFn: () => client.service('assettrak-access').get(userId),
    enabled: !!userId
  })

  const showHistory = canSeeVehicleLocationHistory(assettrakAccess, vehicle)

  const closestCityQuery = {
    query: {
      latitude: vehicleLocation?.latitude,
      longitude: vehicleLocation?.longitude
    }
  }
  const { data: closestCityList } = useQuery({
    queryKey: ['cities', 'find', closestCityQuery],
    queryFn: () => client.service('cities').find(closestCityQuery),
    enabled: !!vehicleLocation
  })

  const closestCity = closestCityList?.length ? closestCityList[0] : undefined

  if (!selectedVehicleId) return null

  const latLng = new google.maps.LatLng(vehicleLocation?.latitude, vehicleLocation?.longitude)

  const renderItem = (item: { title: string | ReactNode; description?: string | ReactNode }) => {
    if (typeof item.description === 'string') {
      return (
        <List.Item>
          <List.Item.Meta title={`${item.title}: ${item.description}`} />
        </List.Item>
      )
    } else {
      return (
        <List.Item>
          <List.Item.Meta title={item.title} description={item.description} />
        </List.Item>
      )
    }
  }

  const items: CollapseProps['items'] = [
    {
      key: 'unit_name',
      label: vehicle?.name,
      children: (
        <List
          size={'small'}
          dataSource={[
            { title: 'Agency', description: agency?.name },
            { title: 'Whole Blood', description: '[Yes - # Units/No]' },
            { title: 'Vehicle Type ', description: vehicleType?.name },
            { title: 'Last Update', description: formatTimestamp(vehicleLocation?.update_received) },
            {
              title: null,
              description: showHistory ? (
                <a href={'#'} onClick={() => setSelectedVehicleHistory(vehicle.vehicle_id)}>
                  History
                </a>
              ) : null
            }
          ]}
          renderItem={renderItem}
        />
      ),
      extra: <Button danger={true} icon={<CloseOutlined />} onClick={() => setSelectedVehicleId(undefined)} />
    },
    {
      key: 'location',
      label: 'Location',
      children: (
        <List
          size={'small'}
          dataSource={[
            {
              title: 'Closest City',
              description: closestCity?.name
            },
            {
              title: 'Coordinates',
              description: (
                <a href={'#'} onClick={() => map?.panTo(latLng)}>
                  {`${vehicleLocation?.latitude.toFixed(4)}, ${vehicleLocation?.longitude.toFixed(4)}`}
                </a>
              )
            },
            { title: 'Speed', description: `${vehicleLocation?.speed}` },
            { title: 'Heading', description: `${getDirection(vehicleLocation?.heading)}` }
          ]}
          renderItem={renderItem}
        />
      )
    },
    {
      key: 'device',
      label: 'Device',
      children: (
        <List
          size={'small'}
          dataSource={[
            { title: 'Device Type', description: deviceType?.name },
            { title: 'Device ID', description: vehicle?.asset_id }
          ]}
          renderItem={renderItem}
        />
      )
    }
  ]

  return (
    <Card
      style={{
        position: 'absolute',
        left: '10px',
        top: '60px',
        zIndex: 3,
        maxHeight: '85vh',
        overflowY: 'auto'
      }}
      styles={{
        header: {
          display: 'none'
        },
        body: {
          padding: 0
        }
      }}
    >
      <Collapse
        style={{ width: '310px' }}
        collapsible={'header'}
        expandIcon={() => null}
        accordion={false}
        activeKey={['unit_name', 'location', 'device']}
        items={items}
      />
    </Card>
  )
}

export default VehicleWindow
